import type { AccountRow, TransactionRow } from './types';
import { resolvePeriod } from './helpers';

export interface BalanceAllocation { period: string; amount: number; kind: 'ib' | 'month' }
export interface BalanceAge {
    status: 'latest_only' | 'older' | 'atypical' | 'unavailable';
    source: 'transactions' | 'none';
    ub: number;
    allocations: BalanceAllocation[];
    year?: number;
    lastMonth?: number;
}

// Owner decision 29.10.2026: assign UB to newest normal-side postings first.
// This is attribution, not a reconciliation of individual open items or raw IB.
export function allocateBalanceAge(row: AccountRow, normalSign: 1 | -1, config: any,
    transactions: TransactionRow[] = []): BalanceAge {
    const result: BalanceAge = { status: 'unavailable', source: 'none', ub: row.ub, allocations: [] };
    if (normalSign * row.ub < 0) return { ...result, status: 'atypical' };
    // Monthly net changes cannot establish which normal-side postings cover UB.
    // Without transaction lines keep the balance warning, with no invented age.
    if (!transactions?.length) return result;
    const period = resolvePeriod(config); // Never infer the audit cutoff from the last posting.
    if (!period) return result;
    result.year = period.year; result.lastMonth = period.monthTo;
    const monthly = new Map<number, number>();
    const add = (month: number, amount: number) => {
        if (month >= 1 && month <= period.monthTo && normalSign * amount > 0) {
            monthly.set(month, (monthly.get(month) || 0) + Math.round(Math.abs(amount) * 100));
        }
    };
    if (transactions?.length) {
        result.source = 'transactions';
        for (const tx of transactions) {
            if (tx.accountNo !== row.accNum) continue;
            const date = String(tx.date || '').slice(0, 10);
            if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(Date.parse(date))
                || new Date(date).toISOString().slice(0, 10) !== date || !Number.isFinite(tx.amount)) return result;
            if (Number(date.slice(0, 4)) === period.year) add(Number(date.slice(5, 7)), tx.amount);
        }
    }
    let remaining = Math.round(Math.abs(row.ub) * 100);
    for (let month = period.monthTo; month >= 1 && remaining > 0; month--) {
        const take = Math.min(remaining, monthly.get(month) || 0);
        if (take > 0) result.allocations.unshift({ period: `${period.year}-${String(month).padStart(2, '0')}`,
            amount: normalSign * take / 100, kind: 'month' });
        remaining -= take;
    }
    if (remaining > 0) result.allocations.unshift({ period: String(period.year - 1), amount: normalSign * remaining / 100, kind: 'ib' });
    result.status = result.allocations.length === 1 && result.allocations[0].period === `${period.year}-${String(period.monthTo).padStart(2, '0')}`
        ? 'latest_only' : 'older';
    return result;
}
